const MODEL_INPUT_SIZE = 512;
const NUM_CLASSES = 1;
const NUM_MASKS = 32;

export function preprocess(img) {
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = MODEL_INPUT_SIZE;
  const ctx = canvas.getContext('2d');
  const scale = Math.min(MODEL_INPUT_SIZE / img.width, MODEL_INPUT_SIZE / img.height);
  const nw = Math.round(img.width * scale);
  const nh = Math.round(img.height * scale);
  const ox = Math.floor((MODEL_INPUT_SIZE - nw) / 2);
  const oy = Math.floor((MODEL_INPUT_SIZE - nh) / 2);
  ctx.fillStyle = '#808080';
  ctx.fillRect(0, 0, MODEL_INPUT_SIZE, MODEL_INPUT_SIZE);
  ctx.drawImage(img, ox, oy, nw, nh);
  const { data } = ctx.getImageData(0, 0, MODEL_INPUT_SIZE, MODEL_INPUT_SIZE);
  const S = MODEL_INPUT_SIZE * MODEL_INPUT_SIZE;
  const tensor = new Float32Array(3 * S);
  for (let i = 0; i < S; i++) {
    tensor[i]         = data[i * 4]     / 255;
    tensor[S + i]     = data[i * 4 + 1] / 255;
    tensor[S * 2 + i] = data[i * 4 + 2] / 255;
  }
  return { tensor, scale, ox, oy };
}

function nms(boxes, scores, iouThresh) {
  const order = Array.from(scores.keys()).sort((a, b) => scores[b] - scores[a]);
  const keep = [];
  const suppressed = new Uint8Array(order.length);
  for (let i = 0; i < order.length; i++) {
    const idx = order[i];
    if (suppressed[idx]) continue;
    keep.push(idx);
    const [x1, y1, x2, y2] = boxes[idx];
    const area1 = (x2 - x1) * (y2 - y1);
    for (let j = i + 1; j < order.length; j++) {
      const jdx = order[j];
      if (suppressed[jdx]) continue;
      const [bx1, by1, bx2, by2] = boxes[jdx];
      const inter =
        Math.max(0, Math.min(x2, bx2) - Math.max(x1, bx1)) *
        Math.max(0, Math.min(y2, by2) - Math.max(y1, by1));
      if (!inter) continue;
      if (inter / (area1 + (bx2 - bx1) * (by2 - by1) - inter) > iouThresh)
        suppressed[jdx] = 1;
    }
  }
  return keep;
}

function sigmoid(x) {
  return 1 / (1 + Math.exp(-x));
}

function decodeMask(maskCoeffs, protoData, protoH, protoW) {
  const mask = new Float32Array(protoH * protoW);
  for (let k = 0; k < NUM_MASKS; k++) {
    const coeff = maskCoeffs[k];
    const offset = k * protoH * protoW;
    for (let i = 0; i < protoH * protoW; i++) mask[i] += coeff * protoData[offset + i];
  }
  for (let i = 0; i < mask.length; i++) mask[i] = sigmoid(mask[i]);
  return mask;
}

export function postprocess(output0, output1, confThresh, iouThresh) {
  const numDet = output0.dims[2];
  const protoH  = output1.dims[2];
  const protoW  = output1.dims[3];
  const det0    = output0.data;
  const proto   = output1.data;

  const boxes = [], scores = [], coeffs = [];

  for (let i = 0; i < numDet; i++) {
    let maxScore = -Infinity;
    for (let c = 0; c < NUM_CLASSES; c++) {
      const s = det0[(4 + c) * numDet + i];
      if (s > maxScore) maxScore = s;
    }
    if (maxScore < confThresh) continue;
    const cx = det0[0 * numDet + i], cy = det0[1 * numDet + i];
    const w  = det0[2 * numDet + i], h  = det0[3 * numDet + i];
    boxes.push([cx - w / 2, cy - h / 2, cx + w / 2, cy + h / 2]);
    scores.push(maxScore);
    const mc = new Float32Array(NUM_MASKS);
    for (let k = 0; k < NUM_MASKS; k++) mc[k] = det0[(4 + NUM_CLASSES + k) * numDet + i];
    coeffs.push(mc);
  }

  if (!boxes.length) return [];

  return nms(boxes, scores, iouThresh).map(idx => {
    const [x1, y1, x2, y2] = boxes[idx];
    const mask = decodeMask(coeffs[idx], proto, protoH, protoW);
    return { box: [x1, y1, x2, y2], score: scores[idx], mask, protoH, protoW };
  });
}

function buildBinaryMaskCanvas(mask, protoW, protoH, imgW, imgH, scale, ox, oy, threshold) {
  const pc = document.createElement('canvas');
  pc.width = protoW; pc.height = protoH;
  const pctx = pc.getContext('2d');
  const pd = pctx.createImageData(protoW, protoH);
  for (let i = 0; i < protoW * protoH; i++) {
    const a = mask[i] > threshold ? 255 : 0;
    pd.data[i * 4]     = 255;
    pd.data[i * 4 + 1] = 255;
    pd.data[i * 4 + 2] = 255;
    pd.data[i * 4 + 3] = a;
  }
  pctx.putImageData(pd, 0, 0);

  const mc = document.createElement('canvas');
  mc.width = mc.height = MODEL_INPUT_SIZE;
  mc.getContext('2d').drawImage(pc, 0, 0, MODEL_INPUT_SIZE, MODEL_INPUT_SIZE);

  const nw = Math.round(imgW * scale);
  const nh = Math.round(imgH * scale);
  const oc = document.createElement('canvas');
  oc.width = imgW; oc.height = imgH;
  oc.getContext('2d').drawImage(mc, ox, oy, nw, nh, 0, 0, imgW, imgH);
  return oc;
}

export function renderToCanvas(detections, img, scale, ox, oy, maskThreshold, outputCanvas, maskCanvas) {
  const W = img.width, H = img.height;
  outputCanvas.width = W; outputCanvas.height = H;
  const ctx = outputCanvas.getContext('2d');
  ctx.drawImage(img, 0, 0);

  if (maskCanvas) { maskCanvas.width = 0; maskCanvas.height = 0; }
  if (!detections.length) return null;

  const { box, score, mask, protoH, protoW } = detections[0];
  const [bx1, by1, bx2, by2] = box;

  const imgX1 = Math.max(0, Math.round((bx1 - ox) / scale));
  const imgY1 = Math.max(0, Math.round((by1 - oy) / scale));
  const imgX2 = Math.min(W, Math.round((bx2 - ox) / scale));
  const imgY2 = Math.min(H, Math.round((by2 - oy) / scale));

  const maskScaled = buildBinaryMaskCanvas(mask, protoW, protoH, W, H, scale, ox, oy, maskThreshold);

  // Green tint overlay
  const tint = document.createElement('canvas');
  tint.width = W; tint.height = H;
  const tctx = tint.getContext('2d');
  tctx.drawImage(maskScaled, 0, 0);
  tctx.globalCompositeOperation = 'source-in';
  tctx.fillStyle = 'rgba(34,197,94,0.45)';
  tctx.fillRect(0, 0, W, H);
  ctx.drawImage(tint, 0, 0);

  // Bounding box + label
  const lw = Math.max(2, Math.round(Math.max(W, H) / 400));
  ctx.strokeStyle = '#22c55e'; ctx.lineWidth = lw;
  ctx.strokeRect(imgX1, imgY1, imgX2 - imgX1, imgY2 - imgY1);
  const fs = Math.max(14, Math.round(W / 60));
  ctx.font = `bold ${fs}px system-ui`;
  const label = `pillow ${(score * 100).toFixed(0)}%`;
  const lx = imgX1 + lw;
  const ly = imgY1 > fs + 4 ? imgY1 - 4 : imgY1 + fs + 4;
  ctx.fillStyle = '#111'; ctx.fillText(label, lx + 1, ly + 1);
  ctx.fillStyle = '#22c55e'; ctx.fillText(label, lx, ly);

  // Cutout (transparent PNG crop)
  if (maskCanvas) {
    const bW = imgX2 - imgX1, bH = imgY2 - imgY1;
    maskCanvas.width = bW; maskCanvas.height = bH;
    const mctx = maskCanvas.getContext('2d');
    mctx.drawImage(img, imgX1, imgY1, bW, bH, 0, 0, bW, bH);
    const cm = document.createElement('canvas');
    cm.width = bW; cm.height = bH;
    cm.getContext('2d').drawImage(maskScaled, imgX1, imgY1, bW, bH, 0, 0, bW, bH);
    mctx.globalCompositeOperation = 'destination-in';
    mctx.drawImage(cm, 0, 0);
    mctx.globalCompositeOperation = 'source-over';
  }

  return { imgX1, imgY1, imgX2, imgY2, score };
}
